import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useApiQuery, useToast } from 'grabbitbot-legacy-ui'
import { screenerClient } from '../../api'
import { useCreditGate } from '../../payment/useCreditGate'
import { CreditOverdueBanner, GatedSaveButton } from '../../payment/CreditGateNotice'
import { LogicOperandPicker } from '../../rules/LogicOperandPicker'
import { LOGIC_OPTIONS, emptyLogicOperand, logicOperandToString, parseLogicOperand, type LogicOperand } from '../../rules/logicOperand'
import type { IndicatorCondition, IndicatorDescriptor } from '../../api/types'

const EMPTY: IndicatorCondition = {
  id: null,
  exchangeId: '',
  tradingType: '',
  name: '',
  indicator: '',
  logic: 'gt',
  logicOperand1: '',
  logicOperand2: '',
}

/**
 * Equivalent of legacy `IndicatorconditionEditComponent` (audit §6).
 * `logicOperand1`/`logicOperand2` are edited with the ported
 * `LogicOperandPicker` (type/field/value/index selects) instead of raw
 * operand-string inputs — same persisted `indicator.field.0` / `constant.5`
 * format as before (`logicOperand.ts`), only the input UX changed. Credit-
 * balance edit gating matches legacy exactly (`useCreditGate`).
 *
 * `IndicatorCondition` has no `source` field in the legacy model at all
 * (`data/model/momentum.ts`) even though the legacy template reads
 * `indicatorCondition.source` — always `undefined` there, so the
 * candle/lastValue field list always showed only "close price". Passing no
 * `source` to `LogicOperandPicker` reproduces that exactly.
 */
export function IndicatorconditionEditPage() {
  const { exchangeId = '', tradingType = '', id = 'new' } = useParams<{
    exchangeId: string
    tradingType: string
    id: string
  }>()
  const isNew = id === 'new'
  const navigate = useNavigate()
  const { showToast } = useToast()
  const gate = useCreditGate()
  const [condition, setCondition] = useState<IndicatorCondition>({ ...EMPTY, exchangeId, tradingType })
  const [logicOperand1, setLogicOperand1] = useState<LogicOperand>(emptyLogicOperand())
  const [logicOperand2, setLogicOperand2] = useState<LogicOperand>(emptyLogicOperand())
  const [saving, setSaving] = useState(false)

  const existing = useApiQuery(
    () => (isNew ? Promise.resolve(null) : screenerClient.viewIndicatorcondition(exchangeId, tradingType, id)),
    [exchangeId, tradingType, id],
  )
  const momentums = useApiQuery(
    () => (isNew ? Promise.resolve([]) : screenerClient.searchIndicatorconditionMomentums(exchangeId, tradingType, id)),
    [exchangeId, tradingType, id, isNew],
  )
  // Legacy always resolves the field list via `searchClosedIndicators`
  // (never `searchIndicators`) for indicator conditions.
  const indicatorList = useApiQuery(
    () => screenerClient.searchClosedIndicators(exchangeId, tradingType, '-', '-'),
    [exchangeId, tradingType],
  )

  useEffect(() => {
    if (existing.data) {
      setCondition(existing.data)
      setLogicOperand1(parseLogicOperand(existing.data.logicOperand1))
      setLogicOperand2(parseLogicOperand(existing.data.logicOperand2))
    }
  }, [existing.data])

  const possibleFields = useMemo(() => {
    const match = (indicatorList.data?.byIndicator ?? []).find(
      (ind: IndicatorDescriptor) => ind.indicator === condition.indicator,
    )
    return match?.fields ?? []
  }, [indicatorList.data, condition.indicator])

  function update<K extends keyof IndicatorCondition>(key: K, value: IndicatorCondition[K]) {
    setCondition((prev) => ({ ...prev, [key]: value }))
  }

  function changeIndicator(value: string) {
    update('indicator', value)
    setLogicOperand1(emptyLogicOperand())
    setLogicOperand2(emptyLogicOperand())
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    setSaving(true)
    const payload: IndicatorCondition = {
      ...condition,
      logicOperand1: logicOperandToString(logicOperand1),
      logicOperand2: logicOperandToString(logicOperand2),
    }
    try {
      if (isNew) {
        const created = await screenerClient.createIndicatorcondition(exchangeId, tradingType, payload)
        showToast('Indicator condition created', 'success')
        navigate(`/${exchangeId}/${tradingType}/indicatorconditions/${created.id}`)
      } else {
        const saved = await screenerClient.modifyIndicatorcondition(exchangeId, tradingType, id, payload)
        setCondition(saved)
        setLogicOperand1(parseLogicOperand(saved.logicOperand1))
        setLogicOperand2(parseLogicOperand(saved.logicOperand2))
        showToast('Indicator condition saved', 'success')
      }
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Could not save indicator condition', 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div>
      <h1>{isNew ? 'New indicator condition' : `Edit indicator condition: ${condition.name}`}</h1>

      <CreditOverdueBanner gate={gate} itemLabel="settings" />

      <form className="screener-form" onSubmit={handleSubmit}>
        <label>
          Name
          <input value={condition.name} onChange={(e) => update('name', e.target.value)} required />
        </label>
        <label>
          Indicator
          <input value={condition.indicator} onChange={(e) => changeIndicator(e.target.value)} required />
        </label>

        <div className="form-group">
          <strong>Condition</strong>
          <LogicOperandPicker
            operand={logicOperand1}
            onChange={setLogicOperand1}
            indicatorName={condition.indicator}
            possibleFields={possibleFields}
          />
          <label>
            Logic
            <select value={condition.logic ?? 'gt'} onChange={(e) => update('logic', e.target.value)}>
              <option value="">choose …</option>
              {LOGIC_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
          <LogicOperandPicker
            operand={logicOperand2}
            onChange={setLogicOperand2}
            indicatorName={condition.indicator}
            possibleFields={possibleFields}
          />
        </div>

        <GatedSaveButton gate={gate} saving={saving} />
      </form>

      {!isNew && (
        <div className="screener-card">
          <h2>Momentums using this condition</h2>
          {momentums.data && momentums.data.length === 0 && <p>None.</p>}
          <ul>
            {(momentums.data ?? []).map((m) => (
              <li key={m.id}>
                <Link to={`/${exchangeId}/${tradingType}/momentums/${m.id}`}>{m.name}</Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
